import React, { useState, useCallback } from "react";
import { Save, Trash2, Play } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import { toast } from "sonner";

// Games without a spatial/progression state don't get cloud save/resume.
const SKIP = ["quiz", "memory", "visualnovel", "sandbox3d"];
const MANUAL = ["1", "2", "3"];

const fmtTime = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
};

// Cloud Save & Resume for the game a player is currently playing (any published game).
export default function GameSaves({ gameId, canvasRef, user, template }) {
  const [open, setOpen] = useState(false);
  const [saves, setSaves] = useState([]);
  const [names, setNames] = useState({ 1: "", 2: "", 3: "" });
  const [busy, setBusy] = useState(false);

  const bySlot = saves.reduce((m, s) => { m[s.slot] = s; return m; }, {});

  const refresh = useCallback(async () => {
    try { const { data } = await api.get(`/games/${gameId}/progress`); setSaves(data.saves || []); }
    catch { setSaves([]); }
  }, [gameId]);

  const onOpenChange = (v) => { setOpen(v); if (v) refresh(); };

  const doSave = async (slot) => {
    const st = await canvasRef.current?.saveState?.();
    if (!st) { toast.error("Play for a moment first, then save your progress."); return; }
    setBusy(true);
    try {
      const name = slot === "auto" ? "Auto-save" : (names[slot]?.trim() || `Slot ${slot}`);
      await api.post(`/games/${gameId}/progress`, { slot, name, state: st });
      toast.success("Progress saved!");
      await refresh();
    } catch { toast.error("Couldn't save progress."); }
    finally { setBusy(false); }
  };

  const doResume = (s) => {
    if (!s?.state) { toast.error("This save looks empty."); return; }
    canvasRef.current?.loadState?.(s.state);
    toast.success(`Resumed “${s.name}”`);
    setOpen(false);
  };

  const doDelete = async (slot) => {
    try { await api.delete(`/games/${gameId}/progress/${slot}`); await refresh(); } catch {}
  };

  if (!user || SKIP.includes(template)) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button variant="secondary" size="sm" data-testid="game-saves-btn"><Save className="w-4 h-4" /> Save / Resume</Button>
      </DialogTrigger>
      <DialogContent className="max-w-md" data-testid="game-saves-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Save className="w-5 h-5" /> Save &amp; Resume</DialogTitle>
          <DialogDescription>Save your progress to the cloud and pick up right where you left off, on any device.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {bySlot.auto && (
            <div className="flex items-center justify-between rounded-xl border border-[hsl(var(--border))] p-3 bg-[hsl(var(--muted))]/40" data-testid="save-slot-auto">
              <div className="min-w-0">
                <p className="font-semibold text-sm">⏱️ Auto-save</p>
                <p className="text-xs text-[hsl(var(--muted-foreground))]">{fmtTime(bySlot.auto.updated_at)} · score {bySlot.auto.score || 0}</p>
              </div>
              <Button size="sm" onClick={() => doResume(bySlot.auto)} data-testid="resume-auto-btn"><Play className="w-4 h-4" /> Resume</Button>
            </div>
          )}
          {MANUAL.map((slot) => {
            const s = bySlot[slot];
            return (
              <div key={slot} className="rounded-xl border border-[hsl(var(--border))] p-3" data-testid={`save-slot-${slot}`}>
                {s ? (
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-semibold text-sm truncate">💾 {s.name}</p>
                      <p className="text-xs text-[hsl(var(--muted-foreground))]">{fmtTime(s.updated_at)} · score {s.score || 0}</p>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <Button size="sm" onClick={() => doResume(s)} data-testid={`resume-${slot}-btn`}><Play className="w-4 h-4" /> Resume</Button>
                      <Button size="sm" variant="ghost" onClick={() => doSave(slot)} disabled={busy} title="Overwrite this slot" data-testid={`overwrite-${slot}-btn`}><Save className="w-4 h-4" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => doDelete(slot)} data-testid={`delete-${slot}-btn`}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Input value={names[slot]} onChange={(e) => setNames((n) => ({ ...n, [slot]: e.target.value }))} placeholder={`Save slot ${slot} name`} maxLength={60} className="h-9" data-testid={`save-name-${slot}`} />
                    <Button size="sm" onClick={() => doSave(slot)} disabled={busy} data-testid={`save-${slot}-btn`}><Save className="w-4 h-4" /> Save</Button>
                  </div>
                )}
              </div>
            );
          })}
          <p className="text-xs text-[hsl(var(--muted-foreground))] text-center pt-1">Auto-save happens when you pause. Manual slots keep up to 3 checkpoints in your account.</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
